import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import emailjs from "@emailjs/browser";
import { MdOutlineEmail } from "react-icons/md";


// Initialize EmailJS with your public key
emailjs.init("NxGdpxSZqeKJltj50");

const Contact = () => {
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState( "" );

  const [usaTime, setUsaTime] = useState(new Date());
  const [germanyTime, setGermanyTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setUsaTime(new Date());

      // Germany is 9 hours ahead of PST (Long Beach, CA)
      const germanyDate = new Date();
      germanyDate.setHours(germanyDate.getHours() + 9);
      setGermanyTime(germanyDate);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getClockAngles = (date) => {
    const hours = date.getHours() % 12;
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    return {
      hourAngle: hours * 30 + minutes * 0.5,
      minuteAngle: minutes * 6,
      secondAngle: seconds * 6,
    };
  };

  const usaAngles = getClockAngles(usaTime);
  const germanyAngles = getClockAngles(germanyTime);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.fullName || !formData.phone || !formData.email || !formData.message) {
      setSubmitStatus({
        type: "error",
        message: "Please fill in all required fields.",
      });
      setTimeout(() => setSubmitStatus({ type: null, message: "" }), 5000);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({
        type: "error",
        message: "Please enter a valid email address.",
      });
      setTimeout(() => setSubmitStatus({ type: null, message: "" }), 5000);
      return;
    }

    setIsSubmitting(true);

    try {
      // Send email using EmailJS
      const templateParams = {
        from_name: formData.fullName,
        from_email: formData.email,
        phone: formData.phone,
        message: formData.message,
        to_name: "Trans World Shipping",
        reply_to: formData.email,
      };

      const result = await emailjs.send(
        "service_gyx5286", // Your Service ID
        "template_s1ipwad", // Your Template ID
        templateParams,
        "NxGdpxSZqeKJltj50" // Your Public Key
      );

      console.log("Email sent successfully:", result.text);
      
      setSubmitStatus({
        type: "success",
        message: "Thank you for your message! We will get back to you soon.",
      });
      
      // Reset form
      setFormData({
        fullName: "",
        phone: "",
        email: "",
        message: "",
      });
      
      // Reset form reference if using form submission
      if (formRef.current) {
        formRef.current.reset();
      }
      
    } catch (error) {
      console.error("Email send error:", error);
      setSubmitStatus({
        type: "error",
        message: "Failed to send message. Please try again later or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus({ type: null, message: "" }), 5000);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | TransWorld Shipping</title>
        <meta
          name="description"
          content="Get in touch with Trans World Shipping for all your logistics needs."
        />
        <link
          rel="canonical"
          href="https://transworldshippingcompany.com/contact"
        />
      </Helmet>

      <TheContact>
        <div className="contact-hero">
          <div className="hero-overlay"></div>
          <div className="hero-content">
            <h1>Contact Us</h1>
            <p>
              Get in touch with Trans World Shipping for all your logistics
              needs.
            </p>
          </div>
        </div>

        <div className="contact-container">
          <div className="contact-grid">
            {/* Form Section */}
            <div className="form-section">
              <h2>Send us a Message</h2>
              <div className="header-line"></div>

              {submitStatus.type === "success" && (
                <div className="success-message">{submitStatus.message}</div>
              )}

              {submitStatus.type === "error" && (
                <div className="error-message">{submitStatus.message}</div>
              )}

              <form ref={formRef} onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label>
                    Phone <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+1 (555) 123-4567"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label>
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label>
                    Message <span className="required">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="How can we help you?"
                    disabled={isSubmitting}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>

            {/* Locations Section */}
            <div className="locations-section">
              <h2>Our Offices</h2>
              <div className="header-line"></div>

              {/* USA Location */}
              <div className="location-card">
                <h3>United States</h3>
                <div className="info-row">
                  <div className="analog-clock">
                    <div className="clock-face">
                      <div
                        className="hour-hand"
                        style={{
                          transform: `rotate(${usaAngles.hourAngle}deg)`,
                        }}
                      ></div>
                      <div
                        className="minute-hand"
                        style={{
                          transform: `rotate(${usaAngles.minuteAngle}deg)`,
                        }}
                      ></div>
                      <div
                        className="second-hand"
                        style={{
                          transform: `rotate(${usaAngles.secondAngle}deg)`,
                        }}
                      ></div>
                      <div className="clock-center"></div>
                      <div className="clock-numbers">
                        <span className="num-12">12</span>
                        <span className="num-3">3</span>
                        <span className="num-6">6</span>
                        <span className="num-9">9</span>
                      </div>
                    </div>
                  </div>
                  <div className="info-details">
                    <div className="clock-time">
                      {usaTime.toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                    <div className="address">
                      <p>Long Beach, CA 90808</p>
                    </div>
                    <div className="contact-info">
                      <a href="tel:+15625550123" className="phone-link">
                        📞 +1 (562) 555-0123
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Germany Location */}
              <div className="location-card">
                <h3>Germany</h3>
                <div className="info-row">
                  <div className="analog-clock">
                    <div className="clock-face">
                      <div
                        className="hour-hand"
                        style={{
                          transform: `rotate(${germanyAngles.hourAngle}deg)`,
                        }}
                      ></div>
                      <div
                        className="minute-hand"
                        style={{
                          transform: `rotate(${germanyAngles.minuteAngle}deg)`,
                        }}
                      ></div>
                      <div
                        className="second-hand"
                        style={{
                          transform: `rotate(${germanyAngles.secondAngle}deg)`,
                        }}
                      ></div>
                      <div className="clock-center"></div>
                      <div className="clock-numbers">
                        <span className="num-12">12</span>
                        <span className="num-3">3</span>
                        <span className="num-6">6</span>
                        <span className="num-9">9</span>
                      </div>
                    </div>
                  </div>
                  <div className="info-details">
                    <div className="clock-time">
                      {germanyTime.toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                    <div className="address">
                      <p>Burchardstrasse 24</p>
                      <p>20095 Hamburg, Germany</p>
                    </div>
                    <div className="contact-info">
                      <a
                        href="https://wa.me/4915123456789"
                        className="whatsapp-link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        💬 WhatsApp +49 151 23456789
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <a
                href="mailto:customer@transworldshipping.delivery"
                className="location-card contact-info"
              >
                <MdOutlineEmail />{" "}
                <span>customer@transworldshipping.delivery</span>
              </a>
            </div>
          </div>
        </div>
      </TheContact>
    </>
  );
};

const TheContact = styled.div`
  width: 100%;
  background: #ffffff;
.contact-info{
  display:flex;
  align-items:center;
  gap:0.5rem;
  font-size:0.8rem;
  text-decoration:none;
  color:#0b1e33;
  span{
    margin-top:-.2rem;
  }
}
  .contact-hero {
    position: relative;
    width: 100%;
    height: 40vh;
    min-height: 300px;
    background: linear-gradient(135deg, #0b1e33 0%, #0a1a2c 100%);

    .hero-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(
        circle at 30% 50%,
        rgba(212, 175, 55, 0.1) 0%,
        transparent 70%
      );
    }

    .hero-content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      color: white;
      width: 100%;
      padding: 0 2rem;
      z-index: 1;

      h1 {
        font-size: 3rem;
        font-weight: 700;
        margin-bottom: 1rem;
      }

      p {
        font-size: 1.2rem;
        opacity: 0.9;
      }
    }
  }

  .contact-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 4rem 2rem;
  }

  .contact-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
  }

  .form-section {
    h2 {
      font-size: 1.8rem;
      color: #0b1e33;
      margin-bottom: 0.5rem;
      font-weight: 700;
    }

    .header-line {
      width: 60px;
      height: 3px;
      background: #d4af37;
      margin-bottom: 2rem;
    }

    .success-message {
      background: #d4edda;
      color: #155724;
      border: 1px solid #c3e6cb;
      border-radius: 8px;
      padding: 1rem;
      margin-bottom: 1.5rem;
      font-size: 0.9rem;
    }

    .error-message {
      background: #f8d7da;
      color: #721c24;
      border: 1px solid #f5c6cb;
      border-radius: 8px;
      padding: 1rem;
      margin-bottom: 1.5rem;
      font-size: 0.9rem;
    }

    .form-group {
      margin-bottom: 1.5rem;

      label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 600;
        color: #0b1e33;

        .required {
          color: #e53e3e;
          font-size: 1.1rem;
        }
      }

      input,
      textarea {
        width: 100%;
        padding: 0.75rem;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        font-size: 1rem;
        font-family: inherit;
        transition: all 0.3s ease;

        &:focus {
          outline: none;
          border-color: #d4af37;
          box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.2);
        }

        &:disabled {
          background-color: #f7fafc;
          cursor: not-allowed;
        }
      }

      textarea {
        resize: vertical;
      }
    }

    .submit-btn {
      background: #d4af37;
      color: #0b1e33;
      border: none;
      padding: 0.9rem 2rem;
      font-size: 1rem;
      font-weight: 600;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.3s ease;
      width: 100%;

      &:hover:not(:disabled) {
        background: #c4a12e;
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(212, 175, 55, 0.3);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }

  .locations-section {
    h2 {
      font-size: 1.8rem;
      color: #0b1e33;
      margin-bottom: 0.5rem;
      font-weight: 700;
    }

    .header-line {
      width: 60px;
      height: 3px;
      background: #d4af37;
      margin-bottom: 2rem;
    }

    .location-card {
      background: #f8f9fa;
      border-radius: 12px;
      padding: 1.25rem;
      margin-bottom: 1.5rem;
      border: 1px solid #e2e8f0;
      transition: all 0.3s ease;

      &:hover {
        border-color: #d4af37;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      }

      h3 {
        font-size: 1.2rem;
        color: #d4af37;
        margin-bottom: 1rem;
        font-weight: 700;
      }

      .info-row {
        display: flex;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;
      }

      .analog-clock {
        width: 80px;
        height: 80px;
        background: white;
        border-radius: 50%;
        border: 2px solid #0b1e33;
        position: relative;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        flex-shrink: 0;

        .clock-face {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .hour-hand {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 22px;
          height: 2px;
          background: #0b1e33;
          transform-origin: left center;
          margin-left: -2px;
          border-radius: 2px;
        }

        .minute-hand {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 30px;
          height: 2px;
          background: #0b1e33;
          transform-origin: left center;
          margin-left: -2px;
          border-radius: 2px;
        }

        .second-hand {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 34px;
          height: 1px;
          background: #e53e3e;
          transform-origin: left center;
          margin-left: -2px;
        }

        .clock-center {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 5px;
          height: 5px;
          background: #d4af37;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          z-index: 3;
        }

        .clock-numbers {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;

          span {
            position: absolute;
            font-size: 7px;
            font-weight: bold;
            color: #0b1e33;

            &.num-12 {
              top: 3px;
              left: 50%;
              transform: translateX(-50%);
            }

            &.num-3 {
              right: 5px;
              top: 50%;
              transform: translateY(-50%);
            }

            &.num-6 {
              bottom: 3px;
              left: 50%;
              transform: translateX(-50%);
            }

            &.num-9 {
              left: 5px;
              top: 50%;
              transform: translateY(-50%);
            }
          }
        }
      }

      .info-details {
        flex: 1;
      }

      .clock-time {
        font-size: 0.9rem;
        font-weight: 600;
        color: #0b1e33;
        font-family: monospace;
        margin-bottom: 0.25rem;
      }

      .address {
        p {
          font-size: 0.8rem;
          color: #4a5568;
          margin-bottom: 0.15rem;
          line-height: 1.4;
        }
      }

      .contact-info {
        margin-top: 0.5rem;

        a {
          font-size: 0.8rem;
          text-decoration: none;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;

          &.phone-link {
            color: #0b1e33;

            &:hover {
              color: #d4af37;
            }
          }

          &.whatsapp-link {
            color: #25d366;

            &:hover {
              color: #128c7e;
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: 1024px) {
    .contact-grid {
      gap: 2rem;
    }
  }

  @media screen and (max-width: 900px) {
    .contact-hero .hero-content h1 {
      font-size: 2.5rem;
    }

    .contact-container {
      padding: 3rem 1.5rem;
    }
  }

  @media screen and (max-width: 768px) {
    .contact-grid {
      grid-template-columns: 1fr;
      gap: 2rem;
    }

    .contact-hero {
      min-height: 250px;

      .hero-content h1 {
        font-size: 2rem;
      }

      .hero-content p {
        font-size: 1rem;
      }
    }

    .form-section h2,
    .locations-section h2 {
      font-size: 1.5rem;
    }
  }

  @media screen and (max-width: 600px) {
    .contact-container {
      padding: 2rem 1rem;
    }

    .location-card .info-row {
      flex-direction: column;
      text-align: center;
    }

    .info-details {
      text-align: center;
    }
  }

  @media screen and (max-width: 480px) {
    .contact-hero .hero-content h1 {
      font-size: 1.5rem;
    }
  }
`;

export default Contact;